/**
 * ESCHER FALLS — 雫（しずく）。遊びの中で貯まる唯一の通貨。
 *
 * ## なぜ「ポイント」でも「コイン」でもないのか
 * 金貨が降ってくる絵はこの作品に無い。水の城で貯まるものは水である。
 * 名前を作品の側に寄せておかないと、UI も演出も後から全部ちぐはぐになる。
 *
 * ## 関門にはしない
 * 「尽きたら遊べない」設計は採らなかった。静かなパズルで進行を人質に取ると、
 * 煩わしさが低評価に直結する。雫は**断れる上乗せ**であり、
 * 持っていなくても最後の面まで普通に遊べる。
 *
 * ## 使い道が無い通貨は報酬にならない
 * 増えるだけの数字は誇りにも動機にもならない。当面の使い道は
 * **エクストラの制限時間の延長**（`REWARD_TIME_MS` と同じ見返り）。
 *
 * **ヒントを売ってはならない。** 盤面を見ずに進める道を金で買えることになり、
 * 放水ボタンを解答オラクルにしなかった判断（CORE-SPEC §3.1）が崩れる。
 * 時間だけが、パズルの答えを一切漏らさない安全な使い道である。
 *
 * ## 保存
 * `records.ts` と同じ流儀で**決して例外を投げない**。読めなければ初期状態に戻す。
 */

import { defaultStore, type RecordStore } from './records';

const KEY = 'escher-falls/wallet/v1';

export interface Wallet {
  /** 手持ちの雫。 */
  readonly drops: number;
  /** 時間回復を最後に精算した時刻（ISO）。 */
  readonly tickedAt: string;
}

/** 面を1つ通したときに貯まる数。 */
export const DROPS_PER_CLEAR = 10;

/** 初めて通した面はさらに上乗せする。二度目以降の周回で稼がせない。 */
export const DROPS_FIRST_CLEAR_BONUS = 15;

/** 広告を見たときの倍率。参照した既存ゲームの「COLLECT x5」に合わせる。 */
export const AD_MULTIPLIER = 5;

/**
 * 章を通したときの褒賞。面1つぶんとは桁を変える。
 * **ここが「クリア報酬」の本体**で、祝いの画面で大きく見せる。
 */
export const DROPS_MAIN_COMPLETE = 100;
export const DROPS_ALL_COMPLETE = 300;

/** 制限時間を30秒延ばすのに要る雫。 */
export const COST_EXTEND_TIME = 40;

/** 時間回復の間隔 (ms)。 */
export const RECOVER_INTERVAL_MS = 30 * 60 * 1000;

/** 時間回復1回ぶんの雫。 */
export const RECOVER_AMOUNT = 5;

/**
 * 時間回復で貯まる上限。
 *
 * **上限を置かないと、しばらく放置するだけで使い道が全部賄えてしまう。**
 * 回復は「触っていない人への小さな戻り」であって、稼ぎ口ではない。
 */
export const RECOVER_CAP = 60;

export const EMPTY_WALLET: Wallet = { drops: 0, tickedAt: '1970-01-01T00:00:00.000Z' };

/**
 * 保存されたものを読む。**無ければ `null`**。
 *
 * 「無い」と「0 で保存されている」を区別するために null を返す。
 * 混ぜると、初めて遊ぶ人の基準時刻が 1970 年になり、
 * 時間回復が上限まで一気に走って**開始時に雫が満タンで配られる**
 * （実際にそうなって、初回起動で 60 入った）。
 */
function readStored(store: RecordStore | null): Wallet | null {
  if (store === null) return null;
  try {
    const raw = store.getItem(KEY);
    if (raw === null) return null;
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return null;
    const o = parsed as Record<string, unknown>;
    const drops = typeof o.drops === 'number' && Number.isFinite(o.drops)
      ? Math.max(0, Math.floor(o.drops))
      : 0;
    const tickedAt = typeof o.tickedAt === 'string' ? o.tickedAt : EMPTY_WALLET.tickedAt;
    return { drops, tickedAt };
  } catch {
    return null;
  }
}

export function saveWallet(w: Wallet, store: RecordStore | null = defaultStore()): void {
  if (store === null) return;
  try {
    store.setItem(KEY, JSON.stringify(w));
  } catch {
    // 保存できない環境でも遊びは続く。
  }
}

/**
 * 時間経過ぶんを精算する。**読み込み時と、画面に戻ったときに呼ぶ。**
 *
 * 経過時間から回復量を求める方式にしてあるのは、アプリが動いていない間の
 * 時間も数えるため。タイマーを回して数えると、閉じている間が止まる。
 *
 * 上限（`RECOVER_CAP`）**より多く持っているときは回復しない**。
 * 回復は「無くなった人への戻り」であって、上限を押し上げる稼ぎ口ではない。
 */
export function settleRecovery(w: Wallet, now: Date): Wallet {
  const last = Date.parse(w.tickedAt);
  const nowMs = now.getTime();
  if (!Number.isFinite(last) || nowMs <= last) {
    return { ...w, tickedAt: now.toISOString() };
  }
  const steps = Math.floor((nowMs - last) / RECOVER_INTERVAL_MS);
  if (steps <= 0) return w;

  // 端数は次回に持ち越す。切り捨てて捨てると、短い起動を繰り返す人が損をする。
  const carried = new Date(last + steps * RECOVER_INTERVAL_MS).toISOString();
  if (w.drops >= RECOVER_CAP) return { ...w, tickedAt: carried };

  const gained = Math.min(steps * RECOVER_AMOUNT, RECOVER_CAP - w.drops);
  return { drops: w.drops + gained, tickedAt: carried };
}

/**
 * 読み込む。時間回復の精算まで済ませて返す。
 * **初めて遊ぶ人には回復を与えない**（基準時刻を「いま」にする）。
 */
export function loadWallet(
  now: Date = new Date(),
  store: RecordStore | null = defaultStore(),
): Wallet {
  const stored = readStored(store);
  if (stored === null) return { drops: 0, tickedAt: now.toISOString() };
  return settleRecovery(stored, now);
}

/** 足す。負の数は無視する（減らすときは `spend` を使う）。 */
export function earn(w: Wallet, amount: number): Wallet {
  if (!Number.isFinite(amount) || amount <= 0) return w;
  return { ...w, drops: w.drops + Math.floor(amount) };
}

/**
 * 使う。足りなければ **`null` を返して何もしない。**
 * 呼び出し側が残高を確かめ忘れても、マイナスにならないようにするため。
 */
export function spend(w: Wallet, amount: number): Wallet | null {
  const cost = Math.max(0, Math.floor(amount));
  if (w.drops < cost) return null;
  return { ...w, drops: w.drops - cost };
}

/** 面を1つ通したときの取り分。初回かどうかで変わる。 */
export function clearReward(firstTime: boolean): number {
  return DROPS_PER_CLEAR + (firstTime ? DROPS_FIRST_CLEAR_BONUS : 0);
}
